import { color, query, space } from '@design/templateFns';
import { Close } from '@icon/Close';
import { ChevronDown, ChevronUp } from '@icon/index';
import { SelectOption } from '@util/types';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
interface SelectProps {
  selectedItem?: SelectOption<string>;
  options: SelectOption<string>[];
  onSelect: ({ value, title }: { value: string; title: string }) => void;
  isSort?: boolean;
  placeholder?: string;
  title?: string;
  label?: string;
}

const Select = ({
  label,
  selectedItem,
  options,
  onSelect,
  isSort = false,
  placeholder,
  title = '',
}: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const onOptionSelection = (data) => {
    onSelect(data);
    setIsOpen(false);
  };
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  return (
    <StyledDropDownWrapper ref={dropdownRef}>
      {isSort ? (
        <StyledSortText
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <InputTitle>{title}</InputTitle> {selectedItem?.title ?? placeholder}
          {isOpen ? <ChevronUp /> : <ChevronDown />}
        </StyledSortText>
      ) : (
        <>
          <StyledSelectWrapper
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          >
            <StyledInputContainer>
              <StyledInput
                placeholder={placeholder}
                value={selectedItem?.title ? selectedItem?.title : ''}
                readOnly
              />
            </StyledInputContainer>
            <StyledIconContainer>
              <svg
                width="11"
                height="8"
                viewBox="0 0 11 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.48437 2L5.48437 6L9.48438 2"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="square"
                />
              </svg>
            </StyledIconContainer>
          </StyledSelectWrapper>
          <StyledNativeSelectWrapper>
            <NativeSelect
              value={selectedItem?.title}
              onChange={(e) =>
                onOptionSelection({
                  title: e.target.value,
                  value: e.target.value,
                })
              }
            >
              {options?.map((data) => (
                <option key={data?.title}>{data?.title}</option>
              ))}
            </NativeSelect>
            <StyledNativeIcon>
              <svg
                width="11"
                height="8"
                viewBox="0 0 11 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.48437 2L5.48437 6L9.48438 2"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="square"
                />
              </svg>
            </StyledNativeIcon>
          </StyledNativeSelectWrapper>
        </>
      )}

      {isOpen ? (
        <StyledDropDownContainer>
          <StyledDropDownItems>
            {label && (
              <MobileVersion>
                {/* figure out to only show on mobile */}
                <StyledDropDownItem>
                  <div>
                    <ul>
                      <StyledLabel>{label}</StyledLabel>
                    </ul>
                    <ShareCloseIcon
                      onClick={() => {
                        setIsOpen(!isOpen);
                      }}
                    >
                      <Close />
                    </ShareCloseIcon>
                  </div>
                </StyledDropDownItem>
              </MobileVersion>
            )}
            {options?.map((data) => (
              <StyledDropDownItem onClick={() => onOptionSelection(data)} key={data?.title}>
                {data?.title}
              </StyledDropDownItem>
            ))}
          </StyledDropDownItems>
        </StyledDropDownContainer>
      ) : (
        <></>
      )}
    </StyledDropDownWrapper>
  );
};

export default Select;

export const ShareCloseIcon = styled.span`
  position: absolute;
  right: 11px;
  top: 11px;
`;

const MobileVersion = styled.div`
  display: none;
  @media screen and (${query.below('tablet')}) {
    display: block;
  }
`;

const StyledLabel = styled.li`
  color: black;
`;

const StyledDropDownItems = styled.ul`
  position: absolute;
  background-color: #fff;
  border: 1px solid #f4f4f4;
  width: 100%;
  border-radius: 0 0 4px 4px;
`;

const StyledDropDownItem = styled.li`
  position: relative;
  height: 32px;
  max-width: 204px;
  display: flex;
  cursor: pointer;
  align-items: center;
  padding: 8px 15px;
  user-select: none;
  border-radius: 0 0 4px 4px;
  white-space: nowrap;
  &:hover {
    background-color: #f4f4f4;
  }
`;
const StyledDropDownContainer = styled.div`
  position: relative;
  top: auto;
  right: 0;
  background-color: white;
  min-width: 188px;
  max-width: 204px;
  z-index: 2;
`;
const StyledDropDownWrapper = styled.div`
  position: relative;
  color: #757575;
  transition: 0.25s;
  border-radius: 0 0 4px 4px;
`;

const InputTitle = styled.span`
  color: ${color('fg')};
  margin-right: ${space('xs')};
  font-weight: 700;
`;
const StyledSelectWrapper = styled.div`
  background-color: white;
  width: 100%;
  height: 48px;
  border: 1px solid ${color('gray1')};
  padding: 0 19px 0 21px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (${query.below('desktop')}) {
    display: none;
  }
`;

const StyledInputContainer = styled.div`
  width: 100%;
`;

const StyledInput = styled.input`
  border: none;
  background: transparent;
  outline: none;
  width: 100%;
  cursor: pointer;
  pointer-events: none;
  user-select: none;
`;

const StyledIconContainer = styled.span``;

const StyledSortText = styled.div`
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  white-space: nowrap;
  position: relative;

  & ~ ${StyledDropDownContainer} {
    position: absolute;
    top: 100%;
    left: 0;
  }

  svg {
    margin-left: ${space('sm')};
    display: inline-block;
  }
`;
const NativeSelect = styled.select`
  background-color: white;
  width: 100%;
  height: 48px;
  border: 1px solid ${color('gray1')};
  padding: 0 19px 0 21px;
  appearance: none;
`;

const StyledNativeSelectWrapper = styled.div`
  display: none;
  align-items: center;

  @media screen and (${query.below('desktop')}) {
    display: flex;
  }
`;

const StyledNativeIcon = styled.span`
  margin-left: -31px;
`;
